import { z } from 'zod';
import { IsoDateTimeSchema, UuidSchema } from './common';
import { IngestionStatusSchema } from './ingestion';
import { PublicationStatusSchema } from './publication';

/**
 * Admin dashboard adat-szerződések.
 * A `metrics` aggregátum a catalog/analytics/ingestion-svc-k összesített KPI-jait fogja.
 * Minden mutatóhoz tartozik egy `delta` érték, ami az előző azonos időintervallumhoz képesti
 * százalékos változás (pl. -3.1 vagy 12.8). Ha a mutató még nem áll rendelkezésre, `null`.
 */
export const DashboardKpiSchema = z.object({
  /**
   * Aktuális időszakra eső érték.
   * `null`, ha a metrikát még nem tudja számolni a rendszer (pl. analytics-svc
   * nincs deployolva). A UI ekkor "—"-t mutat.
   */
  value: z.number().nullable(),
  /** Mértékegység jelzés (pl. `%`, `min`); UI a label mellé teszi */
  unit: z.string().nullable().optional(),
  /** Százalékos változás az előző azonos időszakhoz képest (negatív is lehet) */
  delta: z.number().nullable(),
});
export type DashboardKpi = z.infer<typeof DashboardKpiSchema>;

export const DashboardKpisSchema = z.object({
  /** Publikált kiadványok aktuális darabszáma a tenantban */
  publishedPublications: DashboardKpiSchema,
  /** Aktív olvasók (a vizsgált időszakban legalább 1 munkamenetet kezdő egyedi user) */
  activeReaders: DashboardKpiSchema,
  /** Összes olvasási perc a vizsgált időszakban */
  readingMinutes: DashboardKpiSchema,
  /** Konverziós sikerráta százalékban (completed / total ingestion job) */
  conversionSuccessRate: DashboardKpiSchema,
});
export type DashboardKpis = z.infer<typeof DashboardKpisSchema>;

export const TrafficPointSchema = z.object({
  /** ISO dátum (UTC nap), pl. `2026-04-15` */
  date: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
  /** Az adott napon rögzített olvasási események száma */
  readingEvents: z.number().int().nonnegative(),
  /** Új ingest jobok az adott napon */
  ingestJobs: z.number().int().nonnegative(),
});
export type TrafficPoint = z.infer<typeof TrafficPointSchema>;

export const QueueSnapshotItemSchema = z.object({
  id: UuidSchema,
  filename: z.string(),
  status: IngestionStatusSchema,
  progressPercent: z.number().min(0).max(100),
  updatedAt: IsoDateTimeSchema,
});
export type QueueSnapshotItem = z.infer<typeof QueueSnapshotItemSchema>;

export const RecentPublicationSchema = z.object({
  id: UuidSchema,
  title: z.string(),
  status: PublicationStatusSchema,
  updatedAt: IsoDateTimeSchema,
});
export type RecentPublication = z.infer<typeof RecentPublicationSchema>;

export const DashboardSnapshotSchema = z.object({
  /** Mikor készült a snapshot a backenden — ennek alapján mutatjuk a "frissítve" infót */
  generatedAt: IsoDateTimeSchema,
  /** Az aggregált időablak hossza (napokban), amin a KPI-k és diagramok futnak */
  windowDays: z.number().int().positive(),
  kpis: DashboardKpisSchema,
  traffic: z.array(TrafficPointSchema),
  queue: z.array(QueueSnapshotItemSchema),
  recentPublications: z.array(RecentPublicationSchema),
});
export type DashboardSnapshot = z.infer<typeof DashboardSnapshotSchema>;
